import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ActionDetailModal from '../common/ActionDetailModal';

// MITRE ATT&CK Tactic order for kill chain
const TACTIC_ORDER = [
  'reconnaissance',
  'resource-development',
  'initial-access',
  'execution',
  'persistence',
  'privilege-escalation',
  'defense-evasion',
  'credential-access',
  'discovery',
  'lateral-movement',
  'collection',
  'command-and-control',
  'exfiltration',
  'impact'
];

const TACTIC_COLORS = {
  'reconnaissance': '#9333ea',
  'resource-development': '#7c3aed',
  'initial-access': '#ef4444',
  'execution': '#f97316',
  'persistence': '#eab308',
  'privilege-escalation': '#84cc16',
  'defense-evasion': '#22c55e',
  'credential-access': '#14b8a6',
  'discovery': '#06b6d4',
  'lateral-movement': '#0ea5e9',
  'collection': '#3b82f6',
  'command-and-control': '#6366f1',
  'exfiltration': '#8b5cf6',
  'impact': '#ec4899'
};

const TACTIC_LABELS = {
  'reconnaissance': 'Recon',
  'resource-development': 'Resource Dev',
  'initial-access': 'Initial Access',
  'execution': 'Execution',
  'persistence': 'Persistence',
  'privilege-escalation': 'Priv Esc',
  'defense-evasion': 'Defense Evasion',
  'credential-access': 'Cred Access',
  'discovery': 'Discovery',
  'lateral-movement': 'Lateral Move',
  'collection': 'Collection',
  'command-and-control': 'C2',
  'exfiltration': 'Exfil',
  'impact': 'Impact'
};

function AttackChainVisualization({ actions, playbook, height = 500, onNodeClick, projectId, initialViewMode, hideControls }) {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewMode, setViewMode] = useState(initialViewMode || 'killchain'); // killchain, timeline, graph
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const nodePositionsRef = useRef({});

  // Process data into nodes and edges
  useEffect(() => {
    const processedNodes = [];
    const processedEdges = [];

    // Source data - either actions or playbook steps
    const items = playbook ? playbook.steps : actions;
    if (!items || items.length === 0) return;

    items.forEach((item, index) => {
      const techniqueId = item.mitreAttackTechnique?.id || item.techniqueId || item.technique?.id || item.mitre_technique?.technique_id;
      const tactic = item.mitreAttackTechnique?.tactic || item.tactic || item.technique?.tactics?.[0] || item.mitre_technique?.tactics?.[0] || 'execution';
      const tacticNormalized = tactic.toLowerCase().replace(/ /g, '-').replace(/_/g, '-');

      const node = {
        id: item._id || item.id || `node-${index}`,
        index,
        label: item.title || item.name || `Step ${index + 1}`,
        techniqueId: techniqueId || 'Unknown',
        techniqueName: item.mitreAttackTechnique?.name || item.techniqueName || item.technique?.name || item.mitre_technique?.name || '',
        tactic: tacticNormalized,
        tacticIndex: TACTIC_ORDER.indexOf(tacticNormalized),
        status: item.result || item.status || 'pending',
        timestamp: item.startTimestamp || item.startTime || item.start_time || item.createdAt,
        data: item
      };

      processedNodes.push(node);

      // Create edges between sequential nodes
      if (index > 0) {
        processedEdges.push({
          id: `edge-${index - 1}-${index}`,
          source: processedNodes[index - 1].id,
          target: node.id,
          sourceIndex: index - 1,
          targetIndex: index
        });
      }
    });

    // Sort nodes by tactic order for kill chain view
    processedNodes.sort((a, b) => {
      if (a.tacticIndex === b.tacticIndex) {
        return a.index - b.index;
      }
      return a.tacticIndex - b.tacticIndex;
    });

    setNodes(processedNodes);
    setEdges(processedEdges);
  }, [actions, playbook]);

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  // Draw the visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;

    // Clear canvas
    ctx.fillStyle = darkMode ? '#1f2937' : '#f9fafb';
    ctx.fillRect(0, 0, width, height);

    if (viewMode === 'killchain') {
      drawKillChainView(ctx, width, height);
    } else if (viewMode === 'timeline') {
      drawTimelineView(ctx, width, height);
    } else {
      drawGraphView(ctx, width, height);
    }
  }, [nodes, edges, dimensions, hoveredNode, selectedNode, viewMode, darkMode]);

  // Kill Chain View - Horizontal flow through tactics
  const drawKillChainView = (ctx, width, height) => {
    const padding = 40;
    // Scale down node radius for large datasets
    const nodeRadius = nodes.length > 20 ? 16 : 25;
    const usedTactics = [...new Set(nodes.map(n => n.tactic))].sort(
      (a, b) => TACTIC_ORDER.indexOf(a) - TACTIC_ORDER.indexOf(b)
    );

    // Calculate minimum width needed
    const minLaneWidth = nodes.length > 20 ? 80 : 100;
    const neededWidth = Math.max(width, usedTactics.length * minLaneWidth + padding * 2);
    const laneWidth = (neededWidth - padding * 2) / usedTactics.length;

    // Resize canvas if needed for horizontal scroll
    if (neededWidth > width) {
      ctx.canvas.width = neededWidth;
      ctx.fillStyle = darkMode ? '#1f2937' : '#f9fafb';
      ctx.fillRect(0, 0, neededWidth, height);
    }

    usedTactics.forEach((tactic, i) => {
      const x = padding + i * laneWidth + laneWidth / 2;

      // Lane background
      ctx.fillStyle = TACTIC_COLORS[tactic] + '20';
      ctx.fillRect(padding + i * laneWidth, padding, laneWidth, height - padding * 2);

      // Lane border
      ctx.strokeStyle = TACTIC_COLORS[tactic] + '40';
      ctx.lineWidth = 1;
      ctx.strokeRect(padding + i * laneWidth, padding, laneWidth, height - padding * 2);

      // Tactic label
      ctx.fillStyle = TACTIC_COLORS[tactic];
      ctx.font = nodes.length > 20 ? 'bold 10px sans-serif' : 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(TACTIC_LABELS[tactic] || tactic, x, padding - 10);
    });

    // Calculate node positions
    const positions = {};
    const tacticCounts = {};

    nodes.forEach(node => {
      if (!tacticCounts[node.tactic]) tacticCounts[node.tactic] = 0;
      const tacticIndex = usedTactics.indexOf(node.tactic);
      const x = padding + tacticIndex * laneWidth + laneWidth / 2;
      const nodesInTactic = nodes.filter(n => n.tactic === node.tactic).length;
      const ySpacing = (height - padding * 3) / (nodesInTactic + 1);
      const y = padding * 1.5 + (tacticCounts[node.tactic] + 1) * ySpacing;

      positions[node.id] = { x, y, radius: nodeRadius };
      tacticCounts[node.tactic]++;
    });

    nodePositionsRef.current = positions;

    // Draw edges
    edges.forEach(edge => {
      const sourcePos = positions[edge.source];
      const targetPos = positions[edge.target];
      if (!sourcePos || !targetPos) return;

      drawArrow(ctx, sourcePos.x, sourcePos.y, targetPos.x, targetPos.y, nodeRadius);
    });

    // Draw nodes
    nodes.forEach(node => {
      const pos = positions[node.id];
      if (!pos) return;
      drawNode(ctx, pos.x, pos.y, node, nodeRadius);
    });
  };

  // Timeline View - Horizontal timeline
  const drawTimelineView = (ctx, width, height) => {
    const padding = 60;
    const nodeRadius = 20;
    const timelineY = height / 2;

    // Draw timeline axis
    ctx.strokeStyle = '#4b5563';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, timelineY);
    ctx.lineTo(width - padding, timelineY);
    ctx.stroke();

    // Draw arrow at end
    ctx.beginPath();
    ctx.moveTo(width - padding - 10, timelineY - 5);
    ctx.lineTo(width - padding, timelineY);
    ctx.lineTo(width - padding - 10, timelineY + 5);
    ctx.stroke();

    // Calculate node positions with 4-level staggering to prevent text overlap
    const spacing = (width - padding * 2) / (nodes.length + 1);
    // Cycle: far above, near below, near above, far below
    const yOffsets = [-120, 60, -60, 120];

    const positions = {};

    nodes.forEach((node, i) => {
      const x = padding + (i + 1) * spacing;
      const y = timelineY + yOffsets[i % 4];
      positions[node.id] = { x, y, radius: nodeRadius };

      // Draw connecting line to timeline
      ctx.strokeStyle = TACTIC_COLORS[node.tactic] || '#6b7280';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, timelineY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw node
      drawNode(ctx, x, y, node, nodeRadius);

      // Draw timestamp or index marker on timeline
      ctx.fillStyle = '#9ca3af';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      const isAbove = yOffsets[i % 4] < 0;
      ctx.fillText(`#${i + 1}`, x, timelineY + (isAbove ? 15 : -10));
    });

    nodePositionsRef.current = positions;
  };

  // Graph View - Tactic-grouped matrix layout
  const drawGraphView = (ctx, width, height) => {
    const padding = 60;
    const nodeRadius = nodes.length > 20 ? 16 : 25;

    // Group nodes by tactic
    const usedTactics = [...new Set(nodes.map(n => n.tactic))].sort(
      (a, b) => TACTIC_ORDER.indexOf(a) - TACTIC_ORDER.indexOf(b)
    );

    // Layout: tactics as columns, nodes stacked within each
    const colWidth = (width - padding * 2) / usedTactics.length;
    const positions = {};

    usedTactics.forEach((tactic, colIdx) => {
      const colX = padding + colIdx * colWidth + colWidth / 2;
      const nodesInCol = nodes.filter(n => n.tactic === tactic);
      const rowSpacing = Math.min(60, (height - padding * 3) / (nodesInCol.length + 1));

      // Tactic column header
      ctx.fillStyle = TACTIC_COLORS[tactic] + '15';
      ctx.fillRect(padding + colIdx * colWidth, padding, colWidth, height - padding * 2);
      ctx.fillStyle = TACTIC_COLORS[tactic];
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(TACTIC_LABELS[tactic] || tactic, colX, padding - 8);

      nodesInCol.forEach((node, rowIdx) => {
        const y = padding + 30 + (rowIdx + 1) * rowSpacing;
        positions[node.id] = { x: colX, y, radius: nodeRadius };
      });
    });

    nodePositionsRef.current = positions;

    const centerX = width / 2;
    const centerY = height / 2;

    // Draw edges with curved lines
    edges.forEach(edge => {
      const sourcePos = positions[edge.source];
      const targetPos = positions[edge.target];
      if (!sourcePos || !targetPos) return;

      drawCurvedArrow(ctx, sourcePos.x, sourcePos.y, targetPos.x, targetPos.y, nodeRadius, centerX, centerY);
    });

    // Draw nodes
    nodes.forEach(node => {
      const pos = positions[node.id];
      if (!pos) return;
      drawNode(ctx, pos.x, pos.y, node, nodeRadius);
    });
  };

  // Helper: Draw a node
  const drawNode = (ctx, x, y, node, radius) => {
    const isHovered = hoveredNode?.id === node.id;
    const isSelected = selectedNode?.id === node.id;
    const color = TACTIC_COLORS[node.tactic] || '#6b7280';

    // Status-based styling
    let statusColor = color;
    if (node.status === 'completed' || node.status === 'successful') {
      statusColor = '#22c55e';
    } else if (node.status === 'failed' || node.status === 'blocked') {
      statusColor = '#ef4444';
    } else if (node.status === 'detected') {
      statusColor = '#f59e0b';
    }

    // Node shadow
    if (isHovered || isSelected) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 15;
    }

    // Node circle
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = isSelected ? color : (darkMode ? '#1f2937' : '#ffffff');
    ctx.fill();
    ctx.strokeStyle = isHovered ? '#ffffff' : statusColor;
    ctx.lineWidth = isHovered || isSelected ? 3 : 2;
    ctx.stroke();

    ctx.shadowBlur = 0;

    // Technique ID
    ctx.fillStyle = isSelected ? '#ffffff' : color;
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(node.techniqueId?.substring(0, 6) || '?', x, y);

    // Label below
    ctx.fillStyle = darkMode ? '#d1d5db' : '#374151';
    ctx.font = '10px sans-serif';
    ctx.fillText(truncate(node.label, 15), x, y + radius + 12);

    // Status indicator
    ctx.beginPath();
    ctx.arc(x + radius - 5, y - radius + 5, 6, 0, Math.PI * 2);
    ctx.fillStyle = statusColor;
    ctx.fill();
  };

  // Helper: Draw an arrow
  const drawArrow = (ctx, x1, y1, x2, y2, nodeRadius) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const startX = x1 + nodeRadius * Math.cos(angle);
    const startY = y1 + nodeRadius * Math.sin(angle);
    const endX = x2 - (nodeRadius + 5) * Math.cos(angle);
    const endY = y2 - (nodeRadius + 5) * Math.sin(angle);

    // Line
    ctx.strokeStyle = darkMode ? '#4b5563' : '#9ca3af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Arrowhead
    const arrowLength = 10;
    const arrowAngle = Math.PI / 6;

    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(angle - arrowAngle),
      endY - arrowLength * Math.sin(angle - arrowAngle)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(angle + arrowAngle),
      endY - arrowLength * Math.sin(angle + arrowAngle)
    );
    ctx.stroke();
  };

  // Helper: Draw curved arrow
  const drawCurvedArrow = (ctx, x1, y1, x2, y2, nodeRadius, cx, cy) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const startX = x1 + nodeRadius * Math.cos(angle);
    const startY = y1 + nodeRadius * Math.sin(angle);
    const endX = x2 - (nodeRadius + 5) * Math.cos(angle);
    const endY = y2 - (nodeRadius + 5) * Math.sin(angle);

    // Control point towards center
    const controlX = (startX + endX) / 2 + (cx - (startX + endX) / 2) * 0.3;
    const controlY = (startY + endY) / 2 + (cy - (startY + endY) / 2) * 0.3;

    ctx.strokeStyle = darkMode ? '#4b5563' : '#9ca3af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(controlX, controlY, endX, endY);
    ctx.stroke();

    // Arrowhead
    const t = 0.95;
    const arrowX = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * controlX + t * t * endX;
    const arrowY = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * controlY + t * t * endY;
    const tangentAngle = Math.atan2(endY - arrowY, endX - arrowX);

    const arrowLength = 10;
    const arrowAngle = Math.PI / 6;

    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(tangentAngle - arrowAngle),
      endY - arrowLength * Math.sin(tangentAngle - arrowAngle)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowLength * Math.cos(tangentAngle + arrowAngle),
      endY - arrowLength * Math.sin(tangentAngle + arrowAngle)
    );
    ctx.stroke();
  };

  // Helper: Truncate text
  const truncate = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Get mouse position relative to canvas
  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Handle mouse events
  const handleMouseMove = (e) => {
    const pos = getCanvasPos(e);
    if (!pos) return;

    const positions = nodePositionsRef.current;
    let found = null;

    for (const node of nodes) {
      const np = positions[node.id];
      if (!np) continue;
      const r = np.radius || 25;
      const dx = pos.x - np.x;
      const dy = pos.y - np.y;
      if (dx * dx + dy * dy <= r * r) {
        found = node;
        break;
      }
    }

    setHoveredNode(found || null);
    canvasRef.current.style.cursor = found ? 'pointer' : 'default';
  };

  const handleClick = (e) => {
    const pos = getCanvasPos(e);
    if (!pos) return;

    const positions = nodePositionsRef.current;
    let found = null;

    for (const node of nodes) {
      const np = positions[node.id];
      if (!np) continue;
      const r = np.radius || 25;
      const dx = pos.x - np.x;
      const dy = pos.y - np.y;
      if (dx * dx + dy * dy <= r * r) {
        found = node;
        break;
      }
    }

    if (found) {
      setSelectedNode(found);
      if (onNodeClick) {
        // Parent handles the modal - don't open internal one
        onNodeClick(found.data);
      } else {
        setSelectedAction(found.data);
      }
    } else {
      setSelectedNode(null);
    }
  };

  return (
    <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      {!hideControls && (
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('attackChain.title') || 'Attack Chain'}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('killchain')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'killchain' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {t('attackChain.killChainView') || 'Kill Chain'}
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'timeline' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {t('attackChain.timelineView') || 'Timeline'}
            </button>
            <button
              onClick={() => setViewMode('graph')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'graph' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {t('attackChain.graphView') || 'Graph'}
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      {!hideControls && (
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(TACTIC_COLORS).slice(0, 8).map(([tactic, color]) => (
            <div key={tactic} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{TACTIC_LABELS[tactic]}</span>
            </div>
          ))}
        </div>
      )}

      <div ref={containerRef} className="relative overflow-x-auto">
        <canvas
          ref={canvasRef}
          width={dimensions.width}
          height={dimensions.height}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
          className="rounded"
        />

        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{t('attackChain.noActionsYet') || 'No attack chain data to visualize'}</p>
          </div>
        )}

        {/* Canvas hover tooltip */}
        {hoveredNode && (
          <div
            className="absolute bg-black bg-opacity-90 text-white text-xs px-3 py-2 rounded-lg max-w-xs"
            style={{
              left: Math.min((nodePositionsRef.current[hoveredNode.id]?.x || 0) + 20, dimensions.width - 200),
              top: Math.max((nodePositionsRef.current[hoveredNode.id]?.y || 0) - 40, 10),
              pointerEvents: 'none'
            }}
          >
            <div className="font-bold">{hoveredNode.label}</div>
            {hoveredNode.techniqueName && <div className="text-purple-300">{hoveredNode.techniqueId} - {hoveredNode.techniqueName}</div>}
            <div className="text-gray-300">{hoveredNode.tactic && (TACTIC_LABELS[hoveredNode.tactic] || hoveredNode.tactic)}</div>
            <div className={`mt-1 ${
              hoveredNode.status === 'successful' ? 'text-green-400' :
              hoveredNode.status === 'blocked' ? 'text-red-400' :
              hoveredNode.status === 'detected' ? 'text-yellow-400' : 'text-gray-400'
            }`}>
              {hoveredNode.status}
            </div>
            {/* Affected targets */}
            {hoveredNode.data?.targets && hoveredNode.data.targets.length > 0 && (
              <div className="mt-1 border-t border-gray-700 pt-1">
                {hoveredNode.data.targets.slice(0, 4).map((tgt, i) => {
                  const val = typeof tgt === 'object' ? (tgt.value || tgt.name || tgt._id) : tgt;
                  return <div key={i} style={{ color: '#fb923c' }}>&#8595; {val}</div>;
                })}
                {hoveredNode.data.targets.length > 4 && (
                  <div style={{ color: '#fb923c' }}>+{hoveredNode.data.targets.length - 4} more</div>
                )}
              </div>
            )}
            {/* Sources */}
            {hoveredNode.data?.sources && hoveredNode.data.sources.length > 0 && (
              <div className={`${hoveredNode.data?.targets?.length > 0 ? '' : 'mt-1 border-t border-gray-700 pt-1'}`}>
                {hoveredNode.data.sources.slice(0, 3).map((src, i) => {
                  const val = typeof src === 'object' ? (src.value || src.name || src._id) : src;
                  return <div key={i} style={{ color: '#2dd4bf' }}>&#8593; {val}</div>;
                })}
                {hoveredNode.data.sources.length > 3 && (
                  <div style={{ color: '#2dd4bf' }}>+{hoveredNode.data.sources.length - 3} more</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => { setSelectedAction(null); setSelectedNode(null); }}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
    </div>
  );
}

export default AttackChainVisualization;
